import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, Plus, Eye, Trash2, Send, ShieldCheck, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import CreateAgreementModal from './CreateAgreementModal';
import ViewAgreementModal from './ViewAgreementModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SendForESignModal, { ESignStatusActions } from './SendForESignModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RentAgreements = () => {
  const { token } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAgreements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agreements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreements(response.data);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast({
        title: "Error",
        description: "Failed to fetch rent agreements",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const handleViewAgreement = (agreement) => {
    setSelectedAgreement(agreement);
    setShowViewModal(true);
  };

  const handleDeleteAgreement = (agreement) => {
    setSelectedAgreement(agreement);
    setShowDeleteModal(true);
  };

  const handleSendForESign = (agreement) => {
    setSelectedAgreement(agreement);
    setShowESignModal(true);
  };

  // Re-fetch the list after status refresh / send so the latest leegality_*
  // fields appear without requiring a manual reload.
  const handleAgreementUpdated = () => {
    fetchAgreements();
  };

  const confirmDeleteAgreement = async () => {
    if (!selectedAgreement) return;

    setDeleteLoading(true);

    try {
      await axios.delete(`${API}/agreements/${selectedAgreement.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Agreement Deleted",
        description: "Rent agreement has been deleted successfully"
      });

      fetchAgreements();
      setShowDeleteModal(false);
      setSelectedAgreement(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete agreement",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const stats = {
    total: agreements.length,
    active: agreements.filter(a => a.status === 'active').length,
    expired: agreements.filter(a => a.status === 'expired').length
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading agreements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rent Agreements</h1>
          <p className="text-gray-600 mt-1">Manage and generate rent agreements</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={20} className="mr-2" />
          Create Agreement
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-l-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Agreements</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <FileText className="text-blue-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Active</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.active}</p>
            </div>
            <FileText className="text-green-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-gray-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Expired</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.expired}</p>
            </div>
            <FileText className="text-gray-600" size={32} />
          </div>
        </Card>
      </div>

      {/* Agreements List */}
      {agreements.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Agreements Found</h3>
          <p className="text-gray-600 mb-4">Create your first rent agreement</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={20} className="mr-2" />
            Create Agreement
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {agreements.map((agreement) => (
            <Card key={agreement.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                      <FileText size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{agreement.lessee_name}</h3>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          agreement.status === 'active' ? 'bg-green-100 text-green-800' :
                          agreement.status === 'sent_for_signing' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {agreement.status}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                          Room {agreement.room_number}
                        </span>
                        {agreement.leegality_status && (
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                            agreement.leegality_status === 'completed' || agreement.leegality_status === 'signed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : agreement.leegality_status === 'sent' || agreement.leegality_status === 'in_progress'
                              ? 'bg-amber-100 text-amber-800'
                              : agreement.leegality_status === 'failed' || agreement.leegality_status === 'expired'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {(agreement.leegality_status === 'completed' || agreement.leegality_status === 'signed')
                              ? <CheckCircle2 size={11} />
                              : (agreement.leegality_status === 'failed' || agreement.leegality_status === 'expired')
                              ? <AlertCircle size={11} />
                              : <Clock size={11} />}
                            E-sign: {agreement.leegality_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-gray-600">
                      <span className="font-semibold">Property: </span>
                      <span>{agreement.property_address.split(',')[0]}</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Rent: </span>
                      <span>₹{agreement.monthly_rent}/month</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Deposit: </span>
                      <span>₹{agreement.security_deposit}</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Period: </span>
                      <span>{agreement.lease_start_date} to {agreement.lease_end_date}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap items-start">
                  <Button
                    onClick={() => handleViewAgreement(agreement)}
                    size="sm"
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    data-testid={`view-agreement-${agreement.id}`}
                  >
                    <Eye size={16} className="mr-1" />
                    View
                  </Button>

                  {/* Send for e-sign — only when not yet sent (or sent but failed/expired) */}
                  {(!agreement.leegality_status || ['failed', 'expired'].includes(agreement.leegality_status)) && (
                    <Button
                      onClick={() => handleSendForESign(agreement)}
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid={`send-esign-${agreement.id}`}
                    >
                      <Send size={14} className="mr-1" />
                      Send for e-sign
                    </Button>
                  )}

                  {/* Status refresh + signed-PDF / audit links once sent */}
                  {agreement.leegality_status && !['failed', 'expired'].includes(agreement.leegality_status) && (
                    <ESignStatusActions
                      agreement={agreement}
                      onUpdated={handleAgreementUpdated}
                    />
                  )}

                  <Button
                    onClick={() => handleDeleteAgreement(agreement)}
                    size="sm"
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50"
                    data-testid={`delete-agreement-${agreement.id}`}
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateAgreementModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchAgreements}
        token={token}
      />

      {selectedAgreement && (
        <ViewAgreementModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedAgreement(null);
          }}
          agreement={selectedAgreement}
        />
      )}

      {selectedAgreement && (
        <SendForESignModal
          open={showESignModal}
          onClose={() => {
            setShowESignModal(false);
            setSelectedAgreement(null);
          }}
          agreement={selectedAgreement}
          onSent={handleAgreementUpdated}
        />
      )}

      {selectedAgreement && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedAgreement(null);
          }}
          onConfirm={confirmDeleteAgreement}
          title="Delete Rent Agreement"
          message={`Are you sure you want to delete the rent agreement for ${selectedAgreement.lessee_name}? This action cannot be undone.`}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default RentAgreements;
